import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Briefcase, Building2, Compass, Search, User } from 'lucide-react';
import { useDealDrawer } from '@/features/pipeline/dealDrawerStore';
import { useNewDeal } from '@/features/pipeline/newDealStore';
import type { Deal } from '@/features/pipeline/types';
import { stageLabel } from '@/lib/domain/stages';
import { useOriginadoraDrawer, usePersonDrawer } from '@/features/origination/drawerStores';
import { useEntityPeek } from './entityPeekStore';
import { MIN_SEARCH_LENGTH, type SearchDealHit, totalHits, useGlobalSearch } from './useGlobalSearch';

/** Constrói um Deal mínimo a partir do hit da busca para abrir o drawer (a ficha enriquece via GET). */
function dealFromHit(h: SearchDealHit): Deal {
  return {
    id: h.id,
    code: h.code,
    codename: h.codename,
    stage: h.currentStage,
    auxiliaryStatus: 'Active',
    legalStatus: 'Nenhum',
    stageEnteredAtUtc: new Date().toISOString(),
    initialEntryAtUtc: new Date().toISOString(),
    leadUserId: '',
    leadUserName: '—',
    investmentValueBRL: null,
    checkDiscretionaryBRL: null,
    checkCoinvestmentBRL: null,
    isPriority: false,
    companyId: '',
    companyName: h.companyName ?? '—',
    thesisNames: [],
    teamUserNames: [],
    etag: '',
  };
}

const ROW = 'flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12.5px] hover:bg-vega-bg-2';

/** Busca global da topbar (E7), fiel ao mock app.jsx: dropdown por seções, clique navega/abre. */
export function GlobalSearch() {
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  const openDeal = useDealDrawer((s) => s.openDeal);
  const openPeek = useEntityPeek((s) => s.open);
  const openOriginadora = useOriginadoraDrawer((s) => s.openOriginadora);
  const openPerson = usePersonDrawer((s) => s.openPerson);
  const openNewDeal = useNewDeal((s) => s.openNew);

  // Debounce do termo (evita uma chamada por tecla).
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(q), 220);
    return () => window.clearTimeout(t);
  }, [q]);

  // Fecha ao clicar fora.
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const { data, isFetching } = useGlobalSearch(debounced);
  const hasTerm = debounced.trim().length >= MIN_SEARCH_LENGTH;
  const showDropdown = open && hasTerm;
  const nothing = !isFetching && data != null && totalHits(data) === 0;

  const reset = () => {
    setQ('');
    setDebounced('');
    setOpen(false);
  };

  const goDeal = (h: SearchDealHit) => {
    reset();
    void navigate({ to: '/pipeline' });
    openDeal(dealFromHit(h));
  };

  return (
    <div ref={ref} className="relative flex max-w-[520px] flex-1">
      <div className="flex w-full items-center gap-2 rounded border border-transparent bg-secondary px-2.5 py-1.5 focus-within:border-foreground focus-within:bg-card">
        <Search size={14} className="text-muted-foreground" strokeWidth={1.6} />
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder="Buscar deals, empresas, originadoras, pessoas…"
          className="flex-1 bg-transparent text-[12.5px] outline-none placeholder:text-muted-foreground"
          aria-label="Busca global"
        />
      </div>

      {showDropdown && (
        <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-50 max-h-[380px] overflow-y-auto rounded border border-vega-line bg-vega-surface shadow-vega-md">
          {data && data.deals.length > 0 && (
            <Section title="Deals">
              {data.deals.map((d) => (
                <button type="button" key={d.id} onMouseDown={(e) => e.preventDefault()} onClick={() => goDeal(d)} className={ROW}>
                  <Briefcase size={12} className="text-vega-muted" />
                  <span className="font-semibold">{d.codename}</span>
                  <span className="font-mono text-[10.5px] text-vega-muted-2">{d.code}</span>
                  <span className="ml-auto font-mono text-[10px] uppercase tracking-wide text-vega-muted">
                    {stageLabel(d.currentStage)}
                  </span>
                </button>
              ))}
            </Section>
          )}

          {data && data.companies.length > 0 && (
            <Section title="Empresas">
              {data.companies.map((c) => (
                <button
                  type="button"
                  key={c.id}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    reset();
                    openPeek({ kind: 'company', name: c.name, secondary: c.sectorName });
                  }}
                  className={ROW}
                >
                  <Building2 size={12} className="text-vega-muted" />
                  <span>{c.name}</span>
                  {c.sectorName && <span className="ml-auto text-[11px] text-vega-muted">{c.sectorName}</span>}
                </button>
              ))}
            </Section>
          )}

          {data && data.originators.length > 0 && (
            <Section title="Originadoras">
              {data.originators.map((o) => (
                <button
                  type="button"
                  key={o.id}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    reset();
                    openOriginadora(o.id);
                  }}
                  className={ROW}
                >
                  <Compass size={12} className="text-vega-muted" />
                  <span>{o.name}</span>
                  {o.typeName && <span className="ml-auto text-[11px] text-vega-muted">{o.typeName}</span>}
                </button>
              ))}
            </Section>
          )}

          {data && data.people.length > 0 && (
            <Section title="Pessoas">
              {data.people.map((p) => (
                <button
                  type="button"
                  key={p.id}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    reset();
                    openPerson(p.id);
                  }}
                  className={ROW}
                >
                  <User size={12} className="text-vega-muted" />
                  <span>{p.name}</span>
                  {p.originatorName && <span className="ml-auto text-[11px] text-vega-muted">{p.originatorName}</span>}
                </button>
              ))}
            </Section>
          )}

          {nothing && (
            <div className="px-3 py-3.5 text-[12px] text-vega-muted">
              Nada encontrado.{' '}
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  reset();
                  void navigate({ to: '/pipeline' });
                  openNewDeal();
                }}
                className="font-medium text-vega-info"
              >
                Criar novo Deal
              </button>
              ?
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="border-b border-vega-line-2 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-vega-muted-2">
        {title}
      </div>
      {children}
    </div>
  );
}
